import { useCallback, useEffect } from 'react';

import { useNotifications } from '../../../context/NotificationContext';
import { notificationService } from '../../../services';
import {
  approveCollaborationRequest,
  denyCollaborationRequest,
} from '../../../services/mock/collaborationRequestService.js';
import { getCurrentUserId } from '../../../utils/currentUser.js';
import { filterNotifications } from '../utils/notificationHelpers';

const useNotificationActions = ({ state, setters, navigate }) => {
  const { setFilteredNotifications } = setters;
  const { markAsRead, markAllAsRead } = useNotifications();

  // 🔧 【性能优化】通知数据现在由 NotificationContext 统一管理
  // 标记已读操作使用 Context 提供的方法

  // 筛选通知
  useEffect(() => {
    const filtered = filterNotifications(
      state.items,
      state.activeTab,
      state.timeFilter
    );
    setFilteredNotifications(filtered);
  }, [
    state.items,
    state.activeTab,
    state.timeFilter,
    setFilteredNotifications,
  ]);

  // 处理通知点击，标记为已读
  const handleNotificationClick = useCallback(
    async notification => {
      console.log('Notification clicked:', notification);

      // 如果是 collaboration application 通知，不应该在这里处理跳转
      // 应该在 NotificationItem 中显示审批模态框
      if (
        notification.type === 'collaboration' &&
        notification.meta?.action === 'collaboration_application'
      ) {
        console.log(
          '[useNotificationActions] Collaboration application notification - handled by modal'
        );
        return;
      }

      // 如果是 application_approved 通知，跳转到 collaboration 详情页
      if (
        notification.type === 'collaboration' &&
        notification.meta?.action === 'application_approved'
      ) {
        const collaborationId =
          notification.meta?.collaborationId ||
          notification.projectId ||
          notification.meta?.projectId;

        if (collaborationId) {
          console.log(
            '[useNotificationActions] Application approved notification - navigating to collaboration:',
            collaborationId
          );
          try {
            const { getCollaborationById } = await import(
              '../../../services/collaborationService'
            );
            const result = await getCollaborationById(collaborationId);
            if (result.success && result.data) {
              const collaborationData = result.data;
              if (navigate) {
                navigate(`/tagme/collaboration/${collaborationId}`, {
                  state: { project: collaborationData },
                });
              } else {
                sessionStorage.setItem(
                  `collaboration_${collaborationId}`,
                  JSON.stringify(collaborationData)
                );
                window.location.href = `/tagme/collaboration/${collaborationId}`;
              }
            } else {
              if (navigate) {
                navigate(`/tagme/collaboration/${collaborationId}`);
              } else {
                window.location.href = `/tagme/collaboration/${collaborationId}`;
              }
            }
          } catch (error) {
            console.error(
              '[useNotificationActions] Error navigating to collaboration:',
              error
            );
          }
          // 标记为已读（使用 Context 的方法）
          if (!notification.isRead) {
            await markAsRead(notification.id);
          }
          return;
        }
      }

      // 处理其他 collaboration 类型的通知（非 application）的跳转
      if (notification.type === 'collaboration') {
        // 尝试从不同位置获取项目ID
        const projectId =
          notification.projectId ||
          notification.meta?.projectId ||
          notification.meta?.requestId ||
          notification.meta?.collaborationId;

        console.log('Notification projectId lookup:', {
          direct: notification.projectId,
          metaProjectId: notification.meta?.projectId,
          metaRequestId: notification.meta?.requestId,
          metaCollaborationId: notification.meta?.collaborationId,
          final: projectId,
        });

        if (projectId) {
          try {
            // 先获取完整的 collaboration 数据，确保详情页能正确加载
            const { getCollaborationById } = await import(
              '../../../services/collaborationService'
            );
            const result = await getCollaborationById(projectId);

            if (result.success && result.data) {
              const collaborationData = result.data;
              console.log(
                '[useNotificationActions] Fetched collaboration data:',
                collaborationData
              );

              // 使用 React Router 的 navigate，传递完整数据
              if (navigate) {
                navigate(`/tagme/collaboration/${projectId}`, {
                  state: { project: collaborationData },
                });
                console.log(
                  '[useNotificationActions] Navigated with React Router, passing project data'
                );
              } else {
                // 如果没有 navigate 函数，使用 window.location 并存储到 sessionStorage
                sessionStorage.setItem(
                  `collaboration_${projectId}`,
                  JSON.stringify(collaborationData)
                );
                window.location.href = `/tagme/collaboration/${projectId}`;
                console.log(
                  '[useNotificationActions] Stored collaboration data to sessionStorage and navigating'
                );
              }
            } else {
              console.warn(
                '[useNotificationActions] Failed to fetch collaboration data:',
                result.error
              );
              // 即使获取失败，也尝试跳转（详情页会自己处理数据加载）
              if (navigate) {
                navigate(`/tagme/collaboration/${projectId}`);
              } else {
                window.location.href = `/tagme/collaboration/${projectId}`;
              }
            }
          } catch (error) {
            console.error(
              '[useNotificationActions] Error fetching collaboration data:',
              error
            );
            // 发生错误时，仍然尝试跳转
            if (navigate) {
              navigate(`/tagme/collaboration/${projectId}`);
            } else {
              window.location.href = `/tagme/collaboration/${projectId}`;
            }
          }
        } else {
          console.log('No projectId found in notification:', notification);
        }
      } else {
        // 其他通知类型只标记为已读，不进行跳转
        console.log('Notification clicked, marked as read:', notification.type);
      }
    },
    [navigate]
  );

  // 标记单个通知为已读（使用 Context 的方法）
  const handleMarkAsRead = useCallback(
    async notificationId => {
      try {
        await markAsRead(notificationId);
        console.log(
          'NotificationCenter: Marked notification as read:',
          notificationId
        );
        // 不需要重新加载，Context 会自动更新
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    [markAsRead]
  );

  // 标记所有为已读（使用 Context 的方法）
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      console.log('NotificationCenter: Marked all as read');
      // 不需要重新加载，Context 会自动更新
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  }, [markAllAsRead]);

  // 处理review request审批 - 已移除，改为 Add Experience 功能
  const handleReviewRequest = useCallback(async (notification, action) => {
    console.log(
      'Review request functionality has been replaced with Add Experience'
    );
  }, []);

  // 处理协作请求审批
  const handleCollaborationRequest = useCallback(
    async (notification, action) => {
      try {
        // 如果是 collaboration application，使用不同的处理逻辑
        if (notification.meta?.action === 'collaboration_application') {
          const collaborationId =
            notification.meta?.collaborationId ||
            notification.meta?.projectId ||
            notification.projectId;
          const applicantId =
            notification.meta?.applicantId || notification.senderId;
          const positionId = notification.meta?.applicationData?.positionId;
          const currentUserId = await getCurrentUserId();

          console.log('Handling collaboration application:', {
            collaborationId,
            applicantId,
            positionId,
            action,
            currentUserId,
          });

          // 导入 applicationService
          const applicationService = await import(
            '../../../services/applicationService'
          );

          if (action === 'approve') {
            // 审批申请（这是第一步：批准个人信息申请，允许申请人去申请职位）
            // 注意：这里不需要 positionId，因为这是批准"个人信息申请"，不是批准"职位申请"
            if (collaborationId && applicantId) {
              console.log(
                '[useNotificationActions] ✅ Approving collaboration application (first step)'
              );
              console.log('  - collaborationId:', collaborationId);
              console.log('  - applicantId:', applicantId);
              console.log('  - currentUserId (initiator):', currentUserId);

              // 标记原申请通知为已读
              await markAsRead(notification.id);
              console.log(
                '[useNotificationActions] ✅ Marked original application notification as read'
              );

              // 获取项目信息（用于通知）
              let collaborationTitle = 'Untitled Collaboration';
              let initiatorName = 'Project Owner';
              try {
                const { getCollaborationById } = await import(
                  '../../../services/collaborationService'
                );
                const collabResult = await getCollaborationById(
                  collaborationId
                );
                if (collabResult.success && collabResult.data) {
                  collaborationTitle =
                    collabResult.data.title ||
                    collabResult.data.name ||
                    'Untitled Collaboration';
                }

                // 获取发起者信息
                const { getProfile } = await import('../../../services');
                const initiatorProfile = await getProfile(currentUserId);
                if (initiatorProfile.success && initiatorProfile.data) {
                  initiatorName =
                    initiatorProfile.data.fullName ||
                    initiatorProfile.data.name ||
                    'Project Owner';
                }
              } catch (error) {
                console.warn(
                  '[useNotificationActions] Failed to get collaboration/initiator info:',
                  error
                );
              }

              // 🔥 第一步：更新 Supabase 数据库中的申请状态为 'approved'
              try {
                console.log(
                  '[useNotificationActions] 📝 Updating application status in Supabase...'
                );
                const { supabase } = await import(
                  '../../../services/supabase/client.js'
                );

                // 查找第一步申请记录（role_id 为 null）
                const { data: existingApplication, error: findError } =
                  await supabase
                    .from('collaboration_applications')
                    .select('id')
                    .eq('collaboration_id', collaborationId)
                    .eq('user_id', applicantId)
                    .is('role_id', null) // 第一步申请没有 role_id
                    .maybeSingle();

                if (findError && findError.code !== 'PGRST116') {
                  console.error(
                    '[useNotificationActions] ❌ Error finding application in Supabase:',
                    findError
                  );
                } else if (existingApplication) {
                  // 如果找到了记录，更新状态为 approved
                  const { error: updateError } = await supabase
                    .from('collaboration_applications')
                    .update({ status: 'approved' })
                    .eq('id', existingApplication.id);

                  if (updateError) {
                    console.error(
                      '[useNotificationActions] ❌ Failed to update application status in Supabase:',
                      updateError
                    );
                  } else {
                    console.log(
                      '[useNotificationActions] ✅ Successfully updated application status to "approved" in Supabase'
                    );
                  }
                } else {
                  // 如果没有找到记录，尝试创建一条（向后兼容）
                  console.warn(
                    '[useNotificationActions] ⚠️ Application record not found in Supabase, attempting to create...'
                  );
                  const { applyToCollaboration } = await import(
                    '../../../services/supabase/collaborations'
                  );

                  // 从 notification.meta 获取申请数据
                  const applicationData = {
                    role_id: null,
                    email: notification.meta?.applicationData?.email || '',
                    portfolio:
                      notification.meta?.applicationData?.portfolio || '',
                    message: notification.meta?.applicationData?.message || '',
                    status: 'approved', // 直接创建为 approved
                  };

                  const createResult = await applyToCollaboration(
                    collaborationId,
                    applicantId,
                    applicationData
                  );

                  if (createResult.success) {
                    console.log(
                      '[useNotificationActions] ✅ Successfully created application record as "approved" in Supabase'
                    );
                  } else {
                    console.error(
                      '[useNotificationActions] ❌ Failed to create application record:',
                      createResult.error
                    );
                  }
                }
              } catch (error) {
                console.error(
                  '[useNotificationActions] ❌ Error updating Supabase:',
                  error
                );
              }

              // 发送批准通知给申请人
              console.log(
                '[useNotificationActions] 📤 Sending approval notification to applicant:',
                applicantId
              );
              const approvalNotificationResult =
                await notificationService.createGeneralNotification({
                  userId: applicantId,
                  type: 'collaboration',
                  title: 'Application Approved',
                  message: `Your application for "${collaborationTitle}" has been approved! You can now apply for open positions.`,
                  projectId: collaborationId,
                  meta: {
                    action: 'application_approved',
                    collaborationId: collaborationId,
                    collaborationTitle: collaborationTitle,
                    approvedBy: currentUserId,
                    approvedByName: initiatorName,
                  },
                });

              if (approvalNotificationResult.success) {
                console.log(
                  '[useNotificationActions] ✅ Successfully sent approval notification to applicant'
                );
                // 触发未读通知变化事件，更新申请人的导航栏红点
                window.dispatchEvent(
                  new CustomEvent('notif:unreadChanged', {
                    detail: { userId: applicantId },
                  })
                );
              } else {
                console.error(
                  '[useNotificationActions] ❌ Failed to send approval notification:',
                  approvalNotificationResult.error
                );
              }

              // 重新加载通知列表（发起人的）
              // 🔧 不需要手动更新状态，Context 会自动更新
              // 通过事件通知 Context 重新加载数据
              window.dispatchEvent(
                new CustomEvent('notif:unreadChanged', {
                  detail: { userId: currentUserId },
                })
              );

              // 不跳转，让发起人继续查看通知中心
            } else {
              console.warn(
                '[useNotificationActions] ⚠️ Missing collaborationId or applicantId for approval'
              );
              console.warn('  - collaborationId:', collaborationId);
              console.warn('  - applicantId:', applicantId);
            }
          } else if (action === 'deny') {
            // 拒绝申请
            if (positionId && collaborationId) {
              const result = await applicationService.updateApplicationStatus(
                collaborationId,
                positionId,
                applicantId,
                'rejected'
              );

              if (result.success) {
                console.log(
                  `[useNotificationActions] Application denied:`,
                  result.data
                );
                // 标记通知为已读（使用 Context 的方法）
                await markAsRead(notification.id);

                // 🔧 不需要手动更新状态，Context 会自动更新
                // 通过事件通知 Context 重新加载数据
                window.dispatchEvent(
                  new CustomEvent('notif:unreadChanged', {
                    detail: { userId: currentUserId },
                  })
                );
              } else {
                console.error(
                  '[useNotificationActions] Failed to deny application:',
                  result.error
                );
              }
            } else {
              console.warn(
                '[useNotificationActions] Missing positionId or collaborationId for denial'
              );
            }
          }
        } else {
          // 旧的 collaboration request 处理逻辑
          const { requestId } = notification.meta;
          const currentUserId = await getCurrentUserId();

          console.log('Handling collaboration request:', {
            requestId,
            action,
            currentUserId,
          });

          let updatedRequest;
          if (action === 'approve') {
            updatedRequest = await approveCollaborationRequest(
              requestId,
              currentUserId
            );
          } else if (action === 'deny') {
            updatedRequest = await denyCollaborationRequest(
              requestId,
              currentUserId
            );
          }

          if (updatedRequest) {
            console.log(`Collaboration request ${action}:`, updatedRequest);
            await markAsRead(notification.id);

            // 重新加载通知列表
            // 🔧 不需要手动更新状态，Context 会自动更新
            // 通过事件通知 Context 重新加载数据
            window.dispatchEvent(
              new CustomEvent('notif:unreadChanged', {
                detail: { userId: currentUserId },
              })
            );
          }
        }
      } catch (error) {
        console.error('Error handling collaboration request:', error);
      }
    },
    [navigate]
  );

  return {
    handleNotificationClick,
    handleMarkAsRead,
    handleMarkAllAsRead,
    handleReviewRequest,
    handleCollaborationRequest,
  };
};

export default useNotificationActions;
